/**
 * Adjustment totals math — the client-side mirror of the backend's adjusted
 * totals calculation, used as a fallback when the totals envelope is absent
 * and for live previews (rule editor, close dialog).
 *
 * Rules:
 *  - Every percent adjustment computes against the SERVICES SUBTOTAL only
 *    (adjustments never compound on each other), each rounded to cents.
 *  - Fixed amounts are summed after.
 *  - total = subtotal − discounts + surcharges.
 */
/** Round to 2dp, half away from zero (matches shopspring/decimal rounding). */
export function roundToCents(n) {
    // Snap float noise (1.005 * 100 === 100.49999…) before rounding so exact
    // halves round away from zero as intended.
    const cents = parseFloat((Math.abs(n) * 100).toPrecision(12));
    return ((n < 0 ? -1 : 1) * Math.round(cents)) / 100;
}
/**
 * Dollar magnitude of one adjustment against a services subtotal (always
 * positive; the sign is implied by adjustment_type).
 */
export function adjustmentAmount(servicesSubtotal, adjustment) {
    const value = Number(adjustment.value) || 0;
    if (adjustment.method === 'percent') {
        return roundToCents((servicesSubtotal * value) / 100);
    }
    return roundToCents(value);
}
export function computeAdjustedTotals(servicesSubtotal, adjustments) {
    let discountTotal = 0;
    let surchargeTotal = 0;
    for (const adjustment of adjustments) {
        const amount = adjustmentAmount(servicesSubtotal, adjustment);
        if (adjustment.adjustment_type === 'discount')
            discountTotal += amount;
        else
            surchargeTotal += amount;
    }
    discountTotal = roundToCents(discountTotal);
    surchargeTotal = roundToCents(surchargeTotal);
    return {
        servicesSubtotal: roundToCents(servicesSubtotal),
        discountTotal,
        surchargeTotal,
        total: roundToCents(servicesSubtotal - discountTotal + surchargeTotal),
    };
}
/** Sign prefix for an adjustment type: discounts subtract, surcharges add. */
export function adjustmentSign(type) {
    return type === 'discount' ? '−' : '+';
}
/**
 * "−10%" / "+$50.00" — the configured value of a rule or adjustment,
 * signed by its type.
 */
export function formatAdjustmentValue(adjustment) {
    const value = Number(adjustment.value) || 0;
    const sign = adjustmentSign(adjustment.adjustment_type);
    if (adjustment.method === 'percent') {
        // Trim trailing zeros ("10.5%" not "10.50%").
        return `${sign}${String(parseFloat(value.toFixed(2)))}%`;
    }
    return `${sign}$${value.toFixed(2)}`;
}
/** "−$60.00" / "+$60.00" — a computed dollar amount, signed by its type. */
export function formatSignedAmount(type, amount) {
    return `${adjustmentSign(type)}$${Math.abs(Number(amount) || 0).toFixed(2)}`;
}
